import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FBAInboundContext, STEPS } from './FBAInboundContext';
import StepProgress from './components/shared/StepProgress';
import { PollingOverlay } from './components/shared/PollingIndicator';
import { useOperationPolling } from './hooks/usePolling';

// Step Components
import Step0_PlanSelector from './components/Step0_PlanSelector';
import Step1_AddressAssignment from './components/Step1_AddressAssignment';
import Step2_CreateInboundPlan from './components/Step2_CreateInboundPlan';
import Step3_PackingConfirmation from './components/Step3_PackingConfirmation';
import Step4_PlacementSelection from './components/Step4_PlacementSelection';
import Step5_TransportSelection from './components/Step5_TransportSelection';
import Step6_TrackingDetails from './components/Step6_TrackingDetails';
import Step7_LabelGeneration from './components/Step7_LabelGeneration';

import {
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Search,
  Package,
  ClipboardList,
  RefreshCw,
  Loader2,
  CheckCircle,
  Clock
} from 'lucide-react';
import * as api from './services/fbaInboundApi';

// Helper function to determine plan stage from plan data
// Note: Step numbers are 1-indexed for display (Step 0 is Plan Selector, not shown here)
const getPlanStage = (plan) => {
  // Determine what stage the plan is at
  if (plan.is_locked) {
    if (plan.placement_options?.some(p => p.shipments?.some(s => s.tracking_details))) {
      return { step: 7, label: 'Labels', color: 'green' };
    }
    return { step: 6, label: 'Tracking', color: 'blue' };
  }

  if (plan.placement_options?.some(p => p.is_selected)) {
    return { step: 5, label: 'Transport', color: 'blue' };
  }

  if (plan.placement_options?.length > 0) {
    return { step: 4, label: 'Placement', color: 'yellow' };
  }

  if (plan.is_packing_information_set) {
    return { step: 4, label: 'Placement', color: 'yellow' };
  }

  if (plan.packing_options?.some(p => p.confirmed_at)) {
    return { step: 3, label: 'Packing', color: 'yellow' };
  }

  if (plan.items?.length > 0) {
    return { step: 3, label: 'Packing', color: 'yellow' };
  }

  return { step: 2, label: 'Plan Created', color: 'gray' };
};

// Outbound Search Component for initial selection
const OutboundSelector = ({ onSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activePlans, setActivePlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [activeTab, setActiveTab] = useState('plans'); // 'plans' or 'search'
  const API_URL = import.meta.env.VITE_API_URL;

  // Fetch active plans on mount
  useEffect(() => {
    fetchActivePlans();
  }, []);

  const fetchActivePlans = async () => {
    setLoadingPlans(true);
    try {
      const result = await api.getActivePlans(1, 30);
      // Filter to non-canceled plans and sort by most recent
      const plans = (result.data || [])
        .filter(p => !p.is_canceled)
        .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
      setActivePlans(plans);
    } catch (err) {
      console.error('Failed to fetch active plans:', err);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 3) return;

    setLoading(true);
    try {
      // Use searchTerm parameter (not search) to match the backend endpoint
      const response = await fetch(`${API_URL}/api/get_outbounds?searchTerm=${encodeURIComponent(searchQuery)}`);
      const data = await response.json();
      // Sort by UPDATED_AT descending and limit results
      const sortedData = data
        .sort((a, b) => new Date(b.UPDATED_AT) - new Date(a.UPDATED_AT))
        .slice(0, 20);
      setSearchResults(sortedData);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (plan) => {
    // Navigate to the outbound associated with this plan
    if (plan.outbound_shipment_id) {
      onSelect({ id: plan.outbound_shipment_id, name: plan.name || `Plan ${plan.id}` });
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <Package className="w-16 h-16 text-blue-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          FBA Inbound v2024 Workflow
        </h2>
        <p className="text-gray-600">
          Resume an active plan or start a new inbound workflow
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          onClick={() => setActiveTab('plans')}
          className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'plans'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <ClipboardList className="w-4 h-4 mr-2" />
          Active Plans ({activePlans.length})
        </button>
        <button
          onClick={() => setActiveTab('search')}
          className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'search'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Search className="w-4 h-4 mr-2" />
          New Workflow
        </button>
      </div>

      {/* Active Plans Tab */}
      {activeTab === 'plans' && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-medium text-gray-900">Active FBA Plans</h3>
            <button
              onClick={fetchActivePlans}
              disabled={loadingPlans}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loadingPlans ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {loadingPlans ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-2" />
              <p className="text-gray-500">Loading active plans...</p>
            </div>
          ) : activePlans.length === 0 ? (
            <div className="p-8 text-center">
              <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No active plans found</p>
              <button
                onClick={() => setActiveTab('search')}
                className="mt-3 text-blue-600 hover:text-blue-700 font-medium"
              >
                Start a new workflow
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 max-h-[500px] overflow-y-auto">
              {activePlans.map((plan) => {
                const stage = getPlanStage(plan);
                const itemCount = plan.items?.length || 0;
                const outboundId = plan.outbound_shipment_id;

                return (
                  <button
                    key={plan.id}
                    onClick={() => handlePlanSelect(plan)}
                    className="w-full px-4 py-4 text-left hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900">
                            Plan #{plan.id}
                          </span>
                          {outboundId && (
                            <span className="text-sm text-gray-500">
                              (Outbound #{outboundId})
                            </span>
                          )}
                          {/* Stage Badge */}
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${
                            stage.color === 'green' ? 'bg-green-100 text-green-800' :
                            stage.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                            stage.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {stage.color === 'green' ? (
                              <CheckCircle className="w-3 h-3 mr-1" />
                            ) : (
                              <Clock className="w-3 h-3 mr-1" />
                            )}
                            Step {stage.step}: {stage.label}
                          </span>
                        </div>

                        {plan.name && (
                          <p className="text-sm text-gray-600 mt-1">{plan.name}</p>
                        )}

                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                          {plan.updated_at && (
                            <span>Updated: {new Date(plan.updated_at).toLocaleDateString()}</span>
                          )}
                          {plan.channel_id && (
                            <span>Channel: {plan.channel_id}</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4 mt-1" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search Outbound Shipments
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Search by internal notes or client notes (min 3 characters)
          </p>
          <div className="flex space-x-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search internal or client notes..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={loading || searchQuery.length < 3}
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-4 border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {searchResults.map((outbound) => (
                <button
                  key={outbound.ID}
                  onClick={() => onSelect({ id: outbound.ID, name: outbound.NAME, ...outbound })}
                  className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900">
                        #{outbound.ID} - {outbound.NAME || 'Unnamed Shipment'}
                      </div>
                      {outbound.INTERNAL_NOTES && (
                        <div className="text-sm text-gray-600 truncate">
                          {outbound.INTERNAL_NOTES.substring(0, 80)}
                          {outbound.INTERNAL_NOTES.length > 80 ? '...' : ''}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        Updated: {new Date(outbound.UPDATED_AT).toLocaleDateString()}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Empty state after search */}
          {searchResults.length === 0 && searchQuery.length >= 3 && !loading && (
            <div className="mt-4 text-center py-8 border border-gray-200 rounded-lg">
              <p className="text-gray-500">No outbound shipments found matching "{searchQuery}"</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FBAInboundWizard = () => {
  const navigate = useNavigate();
  const context = useContext(FBAInboundContext);

  const {
    currentStep,
    completedSteps,
    steps,
    nextStep,
    prevStep,
    goToStep,
    loading,
    error,
    clearError,
    outboundId,
    outboundShipment,
    currentOperationId,
    setCurrentOperationId,
    fetchOutboundData,
    refreshPlan,
    maxAccessibleStep
  } = context;

  // Operation polling
  const {
    status: operationStatus,
    elapsedTime,
    isPolling
  } = useOperationPolling(currentOperationId, {
    onSuccess: async (result) => {
      const status = result?.status || result?.operation?.status;
      if (status === 'SUCCESS') {
        setCurrentOperationId(null);
        await refreshPlan();
      }
    },
    onError: () => {
      setCurrentOperationId(null);
    }
  });

  // Handle outbound selection when no outboundId is present
  const handleOutboundSelect = (outbound) => {
    navigate(`/fba-inbound/${outbound.id}`);
  };

  // If no outbound selected, show selector
  if (!outboundId) {
    return (
      <div className="p-8">
        <OutboundSelector onSelect={handleOutboundSelect} />
      </div>
    );
  }

  // Loading state for initial data fetch
  if (loading && !outboundShipment) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading shipment data...</p>
        </div>
      </div>
    );
  }

  // Get current step component
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <Step0_PlanSelector />;
      case 1:
        return <Step1_AddressAssignment />;
      case 2:
        return <Step2_CreateInboundPlan />;
      case 3:
        return <Step3_PackingConfirmation />;
      case 4:
        return <Step4_PlacementSelection />;
      case 5:
        return <Step5_TransportSelection />;
      case 6:
        return <Step6_TrackingDetails />;
      case 7:
        return <Step7_LabelGeneration />;
      default:
        return null;
    }
  };

  const currentStepData = STEPS[currentStep];
  const canGoBack = currentStep > 0;
  const canGoNext = completedSteps.has(currentStep) && currentStep < STEPS.length - 1;

  return (
    <div className="flex flex-col h-full">
      {/* Header with Step Progress */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto">
          <StepProgress
            steps={STEPS}
            currentStep={currentStep}
            completedSteps={completedSteps}
            onStepClick={goToStep}
            maxAccessibleStep={maxAccessibleStep}
          />
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-3">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center text-red-700">
              <AlertCircle className="w-5 h-5 mr-2" />
              <span>{error}</span>
            </div>
            <button
              onClick={clearError}
              className="text-red-700 hover:text-red-800 text-sm font-medium"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-6 py-8">
          {/* Step Header */}
          <div className="mb-6">
            <div className="flex items-center text-sm text-gray-500 mb-1">
              Step {currentStep + 1} of {STEPS.length}
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              {currentStepData.title}
            </h1>
            <p className="text-gray-600 mt-1">
              {currentStepData.description}
            </p>
          </div>

          {/* Step Content */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            {renderStepContent()}
          </div>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button
            onClick={prevStep}
            disabled={!canGoBack || loading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </button>

          <div className="text-sm text-gray-500">
            {outboundShipment && (
              <span>
                Outbound: <span className="font-medium text-gray-900">#{outboundShipment.id}</span>
                {outboundShipment.name && ` - ${outboundShipment.name}`}
              </span>
            )}
          </div>

          <button
            onClick={nextStep}
            disabled={!canGoNext || loading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      </div>

      {/* Polling Overlay */}
      <PollingOverlay
        isVisible={isPolling}
        status={operationStatus}
        title="Processing Request"
        message="Amazon is processing your request. Please wait..."
        elapsedTime={elapsedTime}
        onCancel={() => setCurrentOperationId(null)}
      />
    </div>
  );
};

export default FBAInboundWizard;
